<template>
  <div class="solution-panel">
    <div class="solution-topbar">
      <button type="button" class="solution-back" @click="goBack">
        <v-icon size="18">mdi-arrow-left</v-icon>
        Finding detail
        <span class="solution-crumb">/ Schema solution</span>
      </button>
      <div class="solution-stepper" aria-label="Diagnosis steps">
        <span class="step step-done"><v-icon size="14">mdi-check</v-icon> 1 · Diagnosis</span>
        <button type="button" class="step step-done step-link" @click="goBack">
          <v-icon size="14">mdi-check</v-icon> 2 · Details
        </button>
        <span class="step step-active">3 · Solution</span>
      </div>
    </div>

    <div v-if="loadError" class="solution-notice is-error">
      <div class="notice-title">This run could not be loaded</div>
      <p>{{ loadError }}</p>
      <v-btn variant="outlined" rounded="pill" class="text-none" @click="initialise">Try again</v-btn>
    </div>

    <div v-else-if="!solutionRun" class="solution-notice">
      <v-progress-circular indeterminate size="18" width="2" color="primary" />
      <span class="notice-inline">Starting the schema solution agent…</span>
    </div>

    <template v-else>
      <SchemaSolutionProgress :steps="steps" :page-count="solutionRun.pages_total || 0" />

      <div v-if="runFailed" class="solution-notice is-error">
        <div class="notice-title">The agent stopped before it finished</div>
        <p>{{ solutionRun.error_message || 'No further detail was recorded.' }}</p>
        <v-btn variant="outlined" rounded="pill" class="text-none" @click="restart">Run it again</v-btn>
      </div>

      <div v-else-if="runFoundNothing" class="solution-notice is-quiet">
        <div class="notice-title">There was nothing here to repair</div>
        <p>We no longer find a schema gap on these pages, so there was nothing for us to write. That is a healthy result.</p>
      </div>

      <div v-else-if="runDone && !artifacts.length" class="solution-notice">
        <div class="notice-title">Nothing came back for this finding</div>
        <p>The run finished without writing a block for any page. Run the scan again and try once more.</p>
      </div>

      <template v-else-if="runDone">
        <div class="agent-line">
          <span class="agent-avatar"><v-icon size="17" color="#2169A8">mdi-lightning-bolt</v-icon></span>
          <div class="agent-body">
            <p v-for="(paragraph, index) in narrative" :key="index" class="agent-say">{{ paragraph }}</p>
          </div>
        </div>

        <SchemaSolutionPageTabs
          :pages="pageCards"
          :active-index="pageIndex"
          :counts-label="countsLabel"
          :read-label="readLabel"
          @select-page="selectPage"
        />

        <section v-if="activeView" class="status-card">
          <div class="status-main">
            <div class="brand-eyebrow">Page {{ pageIndex + 1 }} of {{ artifacts.length }} · {{ statusMeta.label }}</div>
            <h2 class="status-headline">{{ statusMeta.headline }}</h2>
            <p class="status-standfirst">{{ statusMeta.standfirst }}</p>
            <a :href="activeView.url" target="_blank" rel="noopener" class="status-url">{{ activeView.url }}</a>
          </div>
          <div class="status-chips">
            <span class="status-chip" :class="severityToneClass">{{ severityLabel }}</span>
            <span class="status-chip is-plain">{{ changeCountLabel }}</span>
          </div>
        </section>

        <section v-if="activeLocked" class="locked-page">
          <div class="locked-placeholder brand-paywall-blur" aria-hidden="true" inert>
            <div class="solution-body">
              <SchemaSolutionMarkup
                :lines="lockedPlaceholder.lines"
                :added-count="lockedPlaceholder.addedCount"
                :unchanged-count="lockedPlaceholder.unchangedCount"
              />
              <SchemaSolutionChangeList
                :changes="lockedPlaceholder.changes"
                :lines="lockedPlaceholder.lines"
              />
            </div>
          </div>
          <div class="locked-prompt">
            <div class="locked-prompt-card">
              <v-icon size="26" class="locked-prompt-icon" aria-hidden="true">mdi-lock-outline</v-icon>
              <h3 class="locked-prompt-title">See the fix for every page</h3>
              <p class="locked-prompt-line">Upgrade to Intelligence to unlock the schema for the rest of these pages.</p>
              <v-btn
                variant="flat"
                class="brand-upgrade-cta-btn text-none"
                rounded="pill"
                size="large"
                @click="$router.push('/profile?tab=billing')"
              >
                Upgrade your plan →
              </v-btn>
            </div>
          </div>
        </section>

        <template v-else>
          <section v-if="installNote" class="install-callout">
            <span class="install-mark">!</span>
            <div>
              <div class="brand-eyebrow">Before you paste this</div>
              <p class="install-note">{{ installNote }}</p>
              <ul v-if="installReplacements.length" class="install-list">
                <li v-for="item in installReplacements" :key="item.position">{{ item.label }}</li>
              </ul>
            </div>
          </section>

          <div v-if="markupLines.length" class="solution-body">
            <SchemaSolutionMarkup
              :lines="markupLines"
              :code="activeCode"
              :added-count="markupCounts.addedCount"
              :removed-count="markupCounts.removedCount"
              :unchanged-count="markupCounts.unchangedCount"
              :has-attribution="hasAttribution"
              :has-before="hasBefore"
              :active-change-id="activeChangeId"
              @select-change="selectChange"
            />
            <SchemaSolutionChangeList
              :changes="activeChanges"
              :lines="markupLines"
              :active-change-id="activeChangeId"
              :contract-version="contractVersion"
              @select-change="selectChange"
            />
          </div>

          <section v-else class="solution-notice is-quiet">
            <div class="notice-title">There is nothing to publish on this page</div>
            <p>What was found on it is recorded below.</p>
          </section>

          <section v-if="showUntouched && untouched.length" class="solution-card is-wide is-quiet">
            <div class="brand-eyebrow">What we did not touch</div>
            <p class="card-standfirst">{{ untouchedSummary }}</p>
            <div class="skipped-grid">
              <div v-for="(item, index) in untouched" :key="index" class="simple-row">
                <div class="simple-title">{{ item.title }}</div>
                <div v-if="item.detail" class="simple-body">{{ item.detail }}</div>
              </div>
            </div>
          </section>

          <div class="solution-grid">
            <section class="solution-card">
              <div class="brand-eyebrow">Every problem, answered</div>
              <p class="card-standfirst">What happened to each issue the diagnosis raised on this page.</p>
              <p v-if="!issues.length" class="card-empty">We recorded nothing to answer on this page.</p>
              <div v-for="issue in issues" :key="issue.key" class="issue-row">
                <span class="issue-chip" :class="`tone-${issue.tone}`">{{ issue.outcomeLabel }}</span>
                <div class="issue-problem">{{ issue.problem }}</div>
                <p class="issue-detail">
                  <strong class="issue-lead">{{ issue.lead }}</strong>
                  <template v-if="issue.body"> {{ issue.body }}</template>
                </p>
                <div v-if="issue.addresses" class="issue-addresses">
                  <div v-for="row in issue.addresses" :key="row.label" class="address-row">
                    <span class="address-label">{{ row.label }}</span>
                    <span class="address-value">{{ row.value }}</span>
                  </div>
                </div>

                <p v-if="issue.supporting" class="issue-supporting">{{ issue.supporting }}</p>
              </div>
            </section>

            <section v-if="flagged.length" class="solution-card">
              <div class="brand-eyebrow">Flagged on a second read</div>
              <p class="card-standfirst">We changed nothing for these. They are doubts, not defects.</p>
              <div v-for="(item, index) in flagged" :key="index" class="flagged-row">
                <div class="flagged-concern">{{ item.concern }}</div>
                <div class="flagged-about">{{ item.about }}</div>
              </div>
            </section>

            <section v-if="needsFromYou.length" class="solution-card">
              <div class="brand-eyebrow">Left undescribed, waiting on you</div>
              <p class="card-standfirst">Named on the page, but not stated clearly enough to assert.</p>
              <div v-for="(item, index) in needsFromYou" :key="index" class="simple-row">
                <div class="simple-title">{{ item.missing }}</div>
                <div class="simple-body">{{ item.we_looked_for }}</div>
              </div>
            </section>

            <section v-if="alsoNoticed.length" class="solution-card">
              <div class="brand-eyebrow">Also noticed</div>
              <p class="card-standfirst">Worth fixing, outside what the diagnosis recorded.</p>
              <div v-for="(item, index) in alsoNoticed" :key="index" class="simple-row">
                <div class="simple-body">{{ displayNoticed(item) }}</div>
              </div>
            </section>
          </div>

          <section v-if="skipped.length" class="solution-card is-wide">
            <div class="card-head">
              <div class="brand-eyebrow">Read but not described</div>
              <span class="card-count">{{ skipped.length }} {{ skipped.length === 1 ? 'section' : 'sections' }}</span>
            </div>
            <p class="card-standfirst">A coverage report, not a to-do list. Tell us if one of these is a fact about this page and we can describe it.</p>
            <div class="skipped-grid">
              <div v-for="(item, index) in skipped" :key="index" class="simple-row">
                <div class="simple-title">{{ item.section }}</div>
                <div class="simple-body">{{ item.why }}</div>
              </div>
            </div>
          </section>
        </template>

        <div class="agent-line tail-line">
          <span class="agent-avatar"><v-icon size="17" color="#2169A8">mdi-lightning-bolt</v-icon></span>
          <div class="agent-body">
            <p class="agent-say">{{ tailCopy }}</p>
            <div v-if="hasNextPage" class="tail-actions">
              <button type="button" class="tail-btn" @click="selectPage(pageIndex + 1)">
                Review {{ pageCards[pageIndex + 1].path }} →
              </button>
            </div>
          </div>
        </div>
      </template>
    </template>

    <div class="solution-dock">
      <div class="dock-inner">
        <v-icon size="19" color="#8496bc">mdi-message-text-outline</v-icon>
        <span class="dock-text">Correcting the agent — “that phone number is the old one”, “which CMS am I using?” — is coming next</span>
        <span class="dock-pill">Soon</span>
      </div>
    </div>
  </div>
</template>

<script>
import axios from '@/axios.js'
import SchemaSolutionChangeList from './SchemaSolutionChangeList.vue'
import SchemaSolutionMarkup from './SchemaSolutionMarkup.vue'
import SchemaSolutionPageTabs from './SchemaSolutionPageTabs.vue'
import SchemaSolutionProgress from './SchemaSolutionProgress.vue'
import { asList } from '@/utils/diagnosisFindings'
import {
  artifactChangeCountFor,
  artifactMetaFor,
  buildMarkupLines,
  contractVersionFor,
  flaggedConcernFor,
  formatCheckedAt,
  installNoteFor,
  installReplacementsFor,
  isTerminalRunStatus,
  issueAddressRowsFor,
  issueCardCopyFor,
  issueOutcomeMetaFor,
  issueProblemFor,
  isLockedArtifact,
  pageCountsFor,
  pageStatusMetaFor,
  plainMarkupLines,
  runNarrativeFor,
  severityLabelFor,
  solutionPagePath,
  solutionRunStepsFor,
  sortIssuesBySeverity,
  untouchedItemFor,
  untouchedSummaryFor
} from '@/utils/solutionSchema'

/** Held back while it reads as an extension of "Read but not described". */
const SHOW_UNTOUCHED = false

/**
 * What sits under the blur on a locked page. Static and made up: a locked page's real
 * markup and changes never reach the client, so nothing here describes the actual page.
 */
const LOCKED_PLACEHOLDER_LINES = [
  { kind: 'unchanged', change: null, text: '<script type="application/ld+json">' },
  { kind: 'unchanged', change: null, text: '{' },
  { kind: 'unchanged', change: null, text: '  "@context": "https://schema.org",' },
  { kind: 'unchanged', change: null, text: '  "@type": "MedicalClinic",' },
  { kind: 'unchanged', change: null, text: '  "name": "Your clinic",' },
  { kind: 'added', change: 'c1', text: '  "telephone": "+1 000 000 0000",' },
  { kind: 'added', change: 'c1', text: '  "email": "hello@example.com",' },
  { kind: 'added', change: 'c2', text: '  "address": {' },
  { kind: 'added', change: 'c2', text: '    "@type": "PostalAddress",' },
  { kind: 'added', change: 'c2', text: '    "streetAddress": "123 Example Street"' },
  { kind: 'added', change: 'c2', text: '  }' },
  { kind: 'unchanged', change: null, text: '}' }
].map((line, index) => ({ ...line, n: index + 1 }))

const LOCKED_PLACEHOLDER = {
  lines: LOCKED_PLACEHOLDER_LINES,
  addedCount: LOCKED_PLACEHOLDER_LINES.filter((line) => line.kind === 'added').length,
  unchangedCount: LOCKED_PLACEHOLDER_LINES.filter((line) => line.kind === 'unchanged').length,
  changes: [
    {
      id: 'c1',
      kind: 'added',
      operation: 'add_property',
      field: 'telephone',
      node: { types: ['MedicalClinic'], name: 'Your clinic' },
      values: [
        {
          field: 'telephone',
          now: '+1 000 000 0000',
          source: { level: 'stated_on_page', quote: 'Call us on +1 000 000 0000', found_in: 'footer' }
        }
      ],
      why: 'The page states this number, so the structured data can say it too.'
    },
    {
      id: 'c2',
      kind: 'added',
      operation: 'add_property',
      field: 'address',
      node: { types: ['MedicalClinic'], name: 'Your clinic' },
      values: [
        {
          field: 'address',
          now: '123 Example Street',
          source: { level: 'stated_on_page', quote: '123 Example Street', found_in: 'body' }
        }
      ],
      why: 'Your address appears on the page but not in its structured data.'
    }
  ]
}

const BASE_URL = process.env.VUE_APP_API_BASE_URL
const POLL_INTERVAL_MS = 3000

export default {
  name: 'SchemaSolutionPanel',
  components: {
    SchemaSolutionChangeList,
    SchemaSolutionMarkup,
    SchemaSolutionPageTabs,
    SchemaSolutionProgress
  },
  props: {
    diagnosisReportId: {
      type: [String, Number],
      default: null
    },
    gapCode: {
      type: String,
      default: 'SCHEMA_GAP'
    }
  },
  data() {
    return {
      solutionRun: null,
      loadError: null,
      pageIndex: 0,
      activeChangeId: null,
      pollingInterval: null,
      pollingInFlight: false,
      fetchId: 0,
      initId: 0
    }
  },
  computed: {
    artifacts() {
      return asList(this.solutionRun && this.solutionRun.artifacts)
    },
    steps() {
      return solutionRunStepsFor(this.solutionRun)
    },
    runDone() {
      return String(this.solutionRun && this.solutionRun.status) === 'done'
    },
    runFailed() {
      return String(this.solutionRun && this.solutionRun.status) === 'failed'
    },
    runFoundNothing() {
      return String(this.solutionRun && this.solutionRun.status) === 'nothing_to_repair'
    },
    narrative() {
      return runNarrativeFor(this.solutionRun, this.artifacts)
    },
    countsLabel() {
      return pageCountsFor(this.artifacts).label
    },
    readLabel() {
      const first = this.artifacts[0]
      return first ? formatCheckedAt(first.client_view && first.client_view.checked_at) : ''
    },
    pageCards() {
      return this.artifacts.map((artifact, index) => {
        const view = (artifact && artifact.client_view) || {}
        const action = String((view.install && view.install.action) || '').toLowerCase()
        return {
          index,
          path: solutionPagePath(view.url),
          action: action === 'add' || action === 'replace' ? action : 'unknown',
          actionLabel: action || 'page',
          severityLabel: severityLabelFor(view.severity),
          meta: artifactMetaFor(artifact).meta
        }
      })
    },
    activeArtifact() {
      return this.artifacts[this.pageIndex] || null
    },
    activeView() {
      return (this.activeArtifact && this.activeArtifact.client_view) || null
    },
    activeLocked() {
      return isLockedArtifact(this.activeArtifact)
    },
    lockedPlaceholder() {
      return LOCKED_PLACEHOLDER
    },
    activeMarkup() {
      return (this.activeView && this.activeView.markup) || null
    },
    activeCode() {
      return (this.activeMarkup && typeof this.activeMarkup.code === 'string') ? this.activeMarkup.code : ''
    },
    builtLines() {
      return buildMarkupLines(this.activeMarkup)
    },
    hasAttribution() {
      return Boolean(this.builtLines)
    },
    hasBefore() {
      return Boolean(this.activeMarkup && this.activeMarkup.before)
    },
    markupLines() {
      if (this.builtLines) return this.builtLines.lines
      return plainMarkupLines(this.activeMarkup)
    },
    markupCounts() {
      if (this.builtLines) return this.builtLines
      const total = this.markupLines.length
      return { addedCount: 0, removedCount: 0, unchangedCount: total }
    },
    contractVersion() {
      return contractVersionFor(this.activeView)
    },
    activeChanges() {
      return asList(this.activeView && this.activeView.changes)
    },
    statusMeta() {
      return pageStatusMetaFor(this.activeView && this.activeView.status, { locked: this.activeLocked })
    },
    severityLabel() {
      return severityLabelFor(this.activeView && this.activeView.severity)
    },
    severityToneClass() {
      const severity = String((this.activeView && this.activeView.severity) || '').toLowerCase()
      if (severity === 'high') return 'is-weak'
      if (severity === 'medium') return 'is-fair'
      return 'is-plain'
    },
    changeCountLabel() {
      const count = artifactChangeCountFor(this.activeArtifact)
      return `${count} ${count === 1 ? 'change' : 'changes'}`
    },
    installNote() {
      const install = this.activeView && this.activeView.install
      return install ? installNoteFor(install) : ''
    },
    installReplacements() {
      const install = this.activeView && this.activeView.install
      return install ? installReplacementsFor(install) : []
    },
    issues() {
      return sortIssuesBySeverity(this.activeView && this.activeView.issues).map((issue, index) => {
        const outcome = issueOutcomeMetaFor(issue && issue.outcome)
        const addresses = issue && issue.addresses
        const copy = issueCardCopyFor(issue)
        return {
          key: `issue-${index}`,
          outcomeLabel: outcome.label,
          tone: outcome.tone,
          problem: issueProblemFor(issue && issue.issue),
          lead: copy.lead,
          body: copy.body,
          supporting: copy.supporting,
          addresses: issueAddressRowsFor(addresses)
        }
      })
    },
    flagged() {
      return asList(this.activeView && this.activeView.flagged).map((item) => ({
        concern: item && item.concern ? flaggedConcernFor(item.concern) : 'worth a second look',
        about: String((item && item.about) || '')
      }))
    },
    needsFromYou() {
      return asList(this.activeView && this.activeView.needs_from_you)
    },
    skipped() {
      return asList(this.activeView && this.activeView.skipped)
    },
    showUntouched() {
      return SHOW_UNTOUCHED
    },
    untouched() {
      return asList(this.activeView && this.activeView.untouched).map(untouchedItemFor)
    },
    untouchedSummary() {
      return untouchedSummaryFor(this.untouched.length)
    },
    alsoNoticed() {
      return asList(this.activeView && this.activeView.also_noticed)
    },
    hasNextPage() {
      return this.pageIndex < this.artifacts.length - 1
    },
    tailCopy() {
      const current = this.pageCards[this.pageIndex]
      // A locked page shows no block, so it is neither "covered" nor ready to paste.
      if (this.activeLocked) {
        return this.hasNextPage
          ? `Next in this finding: ${this.pageCards[this.pageIndex + 1].path}.`
          : 'That is every page this finding names.'
      }
      if (this.hasNextPage) {
        return `That is ${current ? current.path : 'this page'} covered. Next in this finding: ${this.pageCards[this.pageIndex + 1].path}.`
      }
      return 'That is every page this finding names. Paste each block into its page, then re-run the diagnosis to confirm the gap closes.'
    }
  },
  watch: {
    diagnosisReportId() {
      this.initialise()
    }
  },
  mounted() {
    this.initialise()
  },
  beforeUnmount() {
    this.stopPolling()
  },
  methods: {
    authHeaders() {
      return { Authorization: localStorage.getItem('auth_token') }
    },
    goBack() {
      const query = { ...this.$route.query }
      delete query.step
      this.$router.replace({ path: '/analytics', query }).catch(() => {})
    },
    // mounted() and the diagnosisReportId watcher can both call this; two overlapping calls
    // would each find no run and each ask the server to start one.
    async initialise() {
      const initId = (this.initId += 1)

      this.stopPolling()
      this.solutionRun = null
      this.loadError = null
      this.pageIndex = 0
      this.activeChangeId = null

      if (!this.diagnosisReportId) {
        this.loadError = 'This finding has no diagnosis report to solve.'
        return
      }

      try {
        const existing = await this.fetchLatest()
        if (initId !== this.initId) return

        if (existing) {
          this.applyRun(existing)
        } else {
          const created = await this.createRun()
          if (initId !== this.initId) return
          this.applyRun(created)
        }
        this.startPolling()
      } catch (error) {
        if (initId !== this.initId) return
        this.loadError = this.errorMessage(error)
      }
    },
    async restart() {
      this.stopPolling()
      this.loadError = null
      try {
        this.applyRun(await this.createRun())
        this.startPolling()
      } catch (error) {
        this.loadError = this.errorMessage(error)
      }
    },
    async fetchLatest() {
      const response = await axios.get(
        `${BASE_URL}/diagnosis_reports/${this.diagnosisReportId}/solution_runs/latest`,
        { params: { gap_code: this.gapCode }, headers: this.authHeaders() }
      )
      return response.data?.data?.solution_run || null
    },
    async createRun() {
      const response = await axios.post(
        `${BASE_URL}/diagnosis_reports/${this.diagnosisReportId}/solution_runs`,
        { gap_code: this.gapCode },
        { headers: this.authHeaders() }
      )
      return response.data?.data?.solution_run || null
    },
    applyRun(run) {
      if (!run) {
        this.loadError = 'The solution run could not be started.'
        return
      }
      this.solutionRun = run
      if (this.pageIndex > this.artifacts.length - 1) this.pageIndex = 0
    },
    startPolling() {
      this.stopPolling()
      if (!this.solutionRun || isTerminalRunStatus(this.solutionRun.status)) return

      this.pollingInterval = setInterval(async () => {
        if (!this.solutionRun || isTerminalRunStatus(this.solutionRun.status)) {
          this.stopPolling()
          return
        }
        if (this.pollingInFlight) return

        this.pollingInFlight = true
        const fetchId = (this.fetchId += 1)
        try {
          const response = await axios.get(`${BASE_URL}/solution_runs/${this.solutionRun.id}`, {
            headers: this.authHeaders()
          })
          if (fetchId !== this.fetchId) return
          const run = response.data?.data?.solution_run
          if (run) this.applyRun(run)
        } catch (error) {
          console.error('Error polling solution run:', error)
        } finally {
          this.pollingInFlight = false
        }

        if (this.solutionRun && isTerminalRunStatus(this.solutionRun.status)) this.stopPolling()
      }, POLL_INTERVAL_MS)
    },
    stopPolling() {
      if (this.pollingInterval) {
        clearInterval(this.pollingInterval)
        this.pollingInterval = null
      }
    },
    selectPage(index) {
      if (index < 0 || index > this.artifacts.length - 1) return
      this.pageIndex = index
      this.activeChangeId = null
    },
    selectChange(changeId) {
      this.activeChangeId = this.activeChangeId === changeId ? null : changeId
      if (!this.activeChangeId) return
      this.$nextTick(() => {
        const card = document.getElementById(`solution-change-${this.activeChangeId}`)
        if (card && card.parentElement) {
          card.parentElement.scrollTop = card.offsetTop - card.parentElement.offsetTop - 12
        }
      })
    },
    displayNoticed(item) {
      if (typeof item === 'string') return item
      return item?.about || item?.concern || JSON.stringify(item)
    },
    errorMessage(error) {
      if (error?.response?.status === 403) {
        return error.response.data?.error || 'The schema solution agent is part of the Intelligence plan.'
      }
      return error?.response?.data?.error || error?.message || 'Something went wrong.'
    }
  }
}
</script>

<style scoped>
.solution-panel {
  padding-bottom: 120px;
}

.solution-topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 20px;
}

.solution-back {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 0;
  background: transparent;
  color: var(--brand-ocean-600);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  padding: 0;
}

.solution-crumb {
  color: var(--brand-ink-800);
  font-weight: 500;
}

.solution-stepper {
  display: flex;
  gap: 14px;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
}

.step {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: var(--brand-ink-800);
}

.step-done {
  color: #127a6a;
}

.step-active {
  color: var(--brand-ocean-600);
}

.step-link {
  border: 0;
  background: transparent;
  padding: 0;
  cursor: pointer;
  font: inherit;
  font-weight: 600;
}

.solution-notice {
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: flex-start;
  background: #fff;
  border: 1px solid var(--brand-sand-300);
  border-radius: 14px;
  padding: 18px 20px;
  margin-bottom: 16px;
}

.solution-notice.is-error {
  border-color: #e2b4b0;
  background: var(--brand-weak-100);
}

.solution-notice.is-quiet {
  background: var(--brand-sand-100);
}

.notice-title {
  font-weight: 700;
  font-size: 14px;
}

.solution-notice p {
  margin: 0;
  font-size: 13px;
  line-height: 1.55;
  color: var(--brand-ink-800);
}

.notice-inline {
  font-size: 13px;
  color: var(--brand-ink-800);
}

.agent-line {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin: 22px 0 26px;
}

.tail-line {
  margin: 22px 0 8px;
}

.agent-avatar {
  flex-shrink: 0;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: var(--brand-ocean-100);
  display: flex;
  align-items: center;
  justify-content: center;
}

.agent-body {
  padding-top: 4px;
}

.agent-say {
  margin: 0 0 10px;
  font-size: 14px;
  line-height: 1.6;
  max-width: 74ch;
}

.agent-say:last-child {
  margin-bottom: 0;
}

.status-card {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  flex-wrap: wrap;
  background: #fff;
  border: 1px solid var(--brand-sand-300);
  border-radius: 16px;
  padding: 20px;
  margin-bottom: 16px;
}

.status-main {
  min-width: 0;
}

.status-headline {
  font-family: 'Lora', serif;
  font-size: 22px;
  font-weight: 600;
  letter-spacing: -0.02em;
  margin: 8px 0 6px;
}

.status-standfirst {
  margin: 0 0 8px;
  font-size: 14px;
  line-height: 1.55;
  color: var(--brand-ink-800);
  max-width: 62ch;
}

.status-url {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 12.5px;
  word-break: break-all;
  color: var(--brand-ocean-600);
}

.status-chips {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  flex-shrink: 0;
}

.status-chip {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 4px 12px;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
}

.status-chip.is-weak {
  background: var(--brand-weak-100);
  color: #9b2c2c;
}

.status-chip.is-fair {
  background: var(--brand-amber-100);
  color: #92610a;
}

.status-chip.is-plain {
  background: var(--brand-sand-100);
  color: var(--brand-ink-800);
}

.install-callout {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  background: var(--brand-sand-100);
  border: 1px solid var(--brand-sand-300);
  border-radius: 14px;
  padding: 16px;
  margin-bottom: 16px;
}

.install-mark {
  flex-shrink: 0;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: var(--brand-amber-400);
  color: #3d2a00;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 700;
}

.install-note {
  margin: 6px 0 0;
  font-size: 13.5px;
  line-height: 1.55;
}

.install-list {
  margin: 8px 0 0;
  padding-left: 18px;
  font-size: 12.5px;
  line-height: 1.6;
  color: var(--brand-ink-800);
}

.locked-page {
  position: relative;
  margin-bottom: 16px;
}

.locked-placeholder .solution-body {
  margin-bottom: 0;
}

.locked-prompt {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}

.locked-prompt-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  max-width: 420px;
  text-align: center;
  background: #fff;
  border: 1px solid var(--brand-sand-300);
  border-radius: 16px;
  padding: 24px 28px;
  box-shadow: 0 12px 32px rgba(13, 30, 53, 0.14);
}

.locked-prompt-icon {
  color: var(--brand-amber-400) !important;
}

.locked-prompt-title {
  font-family: 'Lora', serif;
  font-size: 20px;
  font-weight: 600;
  letter-spacing: -0.02em;
  margin: 0;
}

.locked-prompt-line {
  margin: 0 0 6px;
  font-size: 14px;
  line-height: 1.55;
  color: var(--brand-ink-800);
}

.solution-body {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(340px, 1fr));
  gap: 16px;
  align-items: start;
  margin-bottom: 28px;
}

.solution-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 16px;
  margin-bottom: 16px;
}

.solution-card {
  background: #fff;
  border: 1px solid var(--brand-sand-300);
  border-radius: 14px;
  padding: 20px;
}

.solution-card.is-wide {
  margin-bottom: 16px;
}

.solution-card.is-quiet {
  background: var(--brand-sand-100);
}

.card-head {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 12px;
}

.card-count {
  font-size: 12px;
  color: #8496bc;
}

.card-standfirst {
  margin: 6px 0 12px;
  font-size: 12px;
  color: #8496bc;
}

.card-empty {
  margin: 0;
  font-size: 13px;
  color: var(--brand-ink-800);
}

.issue-row {
  border: 1px solid var(--brand-sand-300);
  border-radius: 10px;
  background: var(--brand-sand-50);
  padding: 12px 14px;
  margin-bottom: 8px;
}

.issue-chip {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 3px 10px;
  font-size: 11px;
  font-weight: 700;
  margin-bottom: 8px;
}

.issue-chip.tone-positive {
  background: var(--brand-teal-100);
  color: #127a6a;
}

.issue-chip.tone-warn {
  background: var(--brand-amber-100);
  color: #92610a;
}

.issue-chip.tone-neutral {
  background: var(--brand-sand-100);
  color: var(--brand-ink-800);
}

.issue-problem {
  font-size: 13px;
  line-height: 1.5;
  margin-bottom: 6px;
}

.issue-detail {
  margin: 0;
  font-size: 12.5px;
  line-height: 1.5;
  color: var(--brand-ink-800);
}

.issue-lead {
  font-weight: 700;
  color: var(--brand-ink-950);
}

.issue-supporting {
  margin: 6px 0 0;
  font-size: 12px;
  line-height: 1.5;
  color: #8496bc;
}

.issue-addresses {
  margin-top: 10px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.address-row {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  font-size: 12px;
}

.address-label {
  flex-shrink: 0;
  min-width: 110px;
  font-weight: 600;
  color: var(--brand-ink-800);
}

.address-value {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  overflow-wrap: anywhere;
}

.flagged-row {
  border-left: 3px solid var(--brand-amber-400);
  background: var(--brand-amber-100);
  border-radius: 0 8px 8px 0;
  padding: 9px 12px;
  margin-bottom: 8px;
}

.flagged-concern {
  font-size: 11.5px;
  font-weight: 700;
  color: #92610a;
  margin-bottom: 3px;
  text-transform: lowercase;
}

.flagged-about {
  font-size: 12.5px;
  line-height: 1.5;
}

.simple-row {
  border: 1px solid var(--brand-sand-300);
  border-radius: 10px;
  background: var(--brand-sand-50);
  padding: 10px 12px;
  margin-bottom: 8px;
}

.simple-title {
  font-size: 12.5px;
  font-weight: 600;
  margin-bottom: 3px;
}

.simple-body {
  font-size: 11.5px;
  line-height: 1.45;
  color: #8496bc;
}

.skipped-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 8px;
}

.skipped-grid .simple-row {
  margin-bottom: 0;
}

.tail-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 12px;
}

.tail-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid var(--brand-ocean-600);
  border-radius: 999px;
  background: #fff;
  padding: 7px 14px;
  font-size: 12.5px;
  font-weight: 600;
  color: var(--brand-ocean-600);
  cursor: pointer;
}

.solution-dock {
  position: sticky;
  bottom: 0;
  padding: 14px 0 18px;
  background: linear-gradient(to top, var(--brand-sand-50) 62%, rgba(253, 252, 248, 0));
}

.dock-inner {
  border: 1px solid var(--brand-sand-300);
  border-radius: 14px;
  background: var(--brand-sand-100);
  padding: 12px 14px;
  display: flex;
  align-items: center;
  gap: 12px;
  opacity: 0.7;
}

.dock-text {
  flex: 1;
  font-size: 13.5px;
  color: #8496bc;
}

.dock-pill {
  display: inline-flex;
  align-items: center;
  background: #fff;
  border: 1px solid var(--brand-sand-300);
  border-radius: 999px;
  padding: 4px 11px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #8496bc;
}

@media (max-width: 1100px) {
  .solution-body {
    grid-template-columns: 1fr;
  }
}
</style>
